import { useState, useEffect, useCallback, useMemo } from 'react';
import { THEME_REGISTRY, THEME_IDS } from '@/themes/index';
import type { ThemeFamily } from '@/types/theme';
import ThemeCard from './ThemeCard';
import ThemePreview from './ThemePreview';
import ThemeCarouselHero from './ThemeCarouselHero';

const STORAGE_KEY = 'reporter-theme';

type FamilyTab = 'all' | ThemeFamily;

const FAMILY_TABS: { id: FamilyTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'classic-print', label: 'Classic Print' },
  { id: 'digital-native', label: 'Digital-Native' },
  { id: 'immersive-cinematic', label: 'Immersive' },
  { id: 'cultural-regional', label: 'Cultural' },
  { id: 'functional-specialist', label: 'Functional' },
  { id: 'legacy', label: 'Legacy' },
];

interface ThemePickerProps {
  onSelect?: (themeId: string) => void;
  embedded?: boolean;
  liveThemeId?: string;
}

export default function ThemePicker({ onSelect, embedded = false, liveThemeId }: ThemePickerProps) {
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [activeFamily, setActiveFamily] = useState<FamilyTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewId) {
        setPreviewId(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewId]);

  const handleSelect = useCallback(
    (themeId: string) => {
      setSelectedId(themeId);
      localStorage.setItem(STORAGE_KEY, themeId);
      onSelect?.(themeId);
    },
    [onSelect],
  );

  const handleCardClick = useCallback(
    (themeId: string) => {
      handleSelect(themeId);
      setPreviewId(themeId);
    },
    [handleSelect],
  );

  const closePreview = useCallback(() => setPreviewId(null), []);

  const filteredIds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return THEME_IDS.filter((id) => {
      const meta = THEME_REGISTRY[id];
      if (activeFamily !== 'all' && meta.family !== activeFamily) return false;
      if (q) {
        return (
          meta.displayName.toLowerCase().includes(q) ||
          meta.description.toLowerCase().includes(q) ||
          meta.family.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [activeFamily, searchQuery]);

  const previewMeta = previewId ? THEME_REGISTRY[previewId] : null;

  return (
    <div className={embedded ? 'bg-ink-black' : 'min-h-screen bg-ink-black text-on-surface flex flex-col'}>
      {/* ── Carousel Hero ── */}
      <ThemeCarouselHero
        themeIds={filteredIds}
        metaMap={THEME_REGISTRY}
        selectedId={selectedId}
        liveThemeId={liveThemeId}
        onSelect={handleSelect}
      />

      {/* ── Browse section ── */}
      <div className="flex-1 px-4 md:px-6 py-6 flex flex-col gap-5">
        {!embedded && (
          <div className="flex items-baseline gap-3">
            <h1 className="font-headline-md text-[20px] text-on-surface">All Themes</h1>
            <span className="font-meta-code text-[11px] text-meta-gray uppercase tracking-wider">
              {filteredIds.length} available
            </span>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-sm">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-meta-gray text-[16px]">
            search
          </span>
          <input
            type="search"
            placeholder="Search themes…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-surface-container-low border border-border-subtle pl-9 pr-4 py-2 font-meta-code text-meta-code text-on-surface placeholder-meta-gray focus:outline-none focus:border-primary transition-colors"
            style={{ borderRadius: 0 }}
            aria-label="Search themes"
          />
        </div>

        {/* Family tabs */}
        <div role="tablist" aria-label="Theme families" className="flex flex-wrap gap-2">
          {FAMILY_TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeFamily === tab.id}
              onClick={() => setActiveFamily(tab.id)}
              className={`px-3 py-1.5 font-label-caps text-label-caps uppercase border transition-colors focus:outline-none ${
                activeFamily === tab.id
                  ? 'bg-primary text-ink-black border-primary'
                  : 'bg-transparent text-on-surface-variant border-border-subtle hover:border-primary hover:text-primary'
              }`}
              style={{ borderRadius: 0 }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div
          role="listbox"
          aria-label="Theme options"
          aria-activedescendant={selectedId ? `theme-option-${selectedId}` : undefined}
          className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {filteredIds.map((id) => (
            <ThemeCard
              key={id}
              metadata={THEME_REGISTRY[id]}
              isSelected={selectedId === id}
              isLive={liveThemeId === id}
              onClick={() => handleCardClick(id)}
            />
          ))}
        </div>

        {filteredIds.length === 0 && (
          <p className="font-meta-code text-meta-code text-meta-gray text-center py-16 uppercase tracking-widest">
            No themes match your search.
          </p>
        )}
      </div>

      {previewMeta && <ThemePreview metadata={previewMeta} onClose={closePreview} />}
    </div>
  );
}
